import React, { useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setTemplateData } from '../../app/EditorSlice';
import { padServices } from '../../api';
import html2canvas from 'html2canvas';
import { uploadToCloudinary } from '../../utils/cloudinary';
import CompressionConfirmModal from '../ImageCompressionModal/CompressionConfirmModal';
import {
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
  isUploadCancelledError,
  uploadImageWithOptionalCompression,
} from '../../utils/imageUpload';
import './TemplateThumbnail.css';

/** Above global `.ant-modal` z-index (see `index.css`) so confirm sits above thumbnail dialog. */
const THUMBNAIL_COMPRESS_MODAL_Z = 10000001;

// Function to create a default thumbnail
const createDefaultThumbnail = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 300;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 300, 200);
  ctx.fillStyle = '#cccccc';
  ctx.font = '24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Default Thumbnail', 150, 100);
  return canvas.toDataURL('image/png');
};

const TemplateThumbnail = ({ viewMode, onMenuClick }) => {

  const dispatch = useDispatch();
  const { id } = useParams();
  const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
  const templateData = useSelector((state) => state.editor.templateData);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [compressConfirmState, setCompressConfirmState] = useState({
    open: false,
    fileSize: 0,
    resolver: null,
  });
  const existingThumbnail = templateData?.thumbnail || createDefaultThumbnail();

  const askToCompress = (file) =>
    new Promise((resolve) => {
      setCompressConfirmState({
        open: true,
        fileSize: file.size,
        resolver: resolve,
      });
    });

  const resolveCompressChoice = (choice) => {
    setCompressConfirmState((prev) => {
      if (prev.resolver) prev.resolver(choice);
      return { open: false, fileSize: 0, resolver: null };
    });
  };

  const handleOpenModal = () => {
    setPreviewUrl(null);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    try {
      setIsCapturing(true);
      message.loading({ content: 'Capturing and uploading thumbnail...', key: 'capture' });

      // Wait to ensure everything is rendered
      await new Promise(resolve => setTimeout(resolve, 500));

      // Target the actual editor div with content
      const editorElement = document.querySelector('#lexical-editor-container .ContentEditable__root');
      
      if (!editorElement) {
        message.error({ content: 'Editor content not found', key: 'capture' });
        return;
      }

      // Calculate first page height (letter size ratio)
      const width = editorElement.scrollWidth;
      const pageHeight = Math.min(editorElement.scrollHeight, width * 1.294);

      const canvas = await html2canvas(editorElement, {
        scale: 0.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: width,
        height: pageHeight,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc, clonedElement) => {
          // Ensure visibility
          clonedElement.style.display = 'block';
          clonedElement.style.visibility = 'visible';
          clonedElement.style.opacity = '1';
          clonedElement.style.height = pageHeight + 'px';
          
          // Remove zero-sized background images
          const allElements = clonedElement.querySelectorAll('*');
          allElements.forEach(el => {
            if (el.clientWidth === 0 || el.clientHeight === 0) {
              el.style.backgroundImage = 'none';
            }
          });
        },
      });

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Generated canvas has zero dimensions');
      }

      const thumbnailDataUrl = canvas.toDataURL('image/png', 0.85);
      const cloudinaryUrl = await uploadToCloudinary(thumbnailDataUrl);
      setPreviewUrl(cloudinaryUrl);
      message.success({ content: 'Thumbnail captured successfully!', key: 'capture' });
    } catch (error) {
      console.error('Failed to capture thumbnail:', error);
      message.error({ content: error?.message || 'Failed to capture thumbnail', key: 'capture' });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      const thumbnailUrl = customUrl || previewUrl;
      
      if (!thumbnailUrl) {
        message.warning('Please capture or upload a thumbnail first');
        return;
      }

      const payload = {
        data: { thumbnail: thumbnailUrl },
        templateId: id,
        dispatch,
      };

      const res = await padServices.updateTemplate(payload);
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      // Update templateData in store
      if (res?.data) {
        dispatch(setTemplateData({ ...templateData, ...res.data, thumbnail: thumbnailUrl }));
      } else {
        // Fallback: update locally
        dispatch(setTemplateData({ ...templateData, thumbnail: thumbnailUrl }));
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    }
  };

  const handleCancel = () => {
    setCompressConfirmState((prev) => {
      if (prev.resolver) prev.resolver(null);
      return { open: false, fileSize: 0, resolver: null };
    });
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = async (file) => {
    try {
      setIsUploading(true);

      if (file.size <= DEFAULT_COMPRESS_THRESHOLD_BYTES) {
        message.loading({ content: 'Uploading image...', key: 'upload' });
      }

      const cloudinaryUrl = await uploadImageWithOptionalCompression(file, {
        thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress: async (largeFile) => {
          message.destroy('upload');
          const choice = await askToCompress(largeFile);
          if (choice === null) {
            return null;
          }
          message.loading({
            content: choice ? 'Compressing and uploading image...' : 'Uploading original image...',
            key: 'upload',
          });
          return choice;
        },
      });

      setCustomUrl(cloudinaryUrl);
      message.success({ content: 'Image uploaded successfully!', key: 'upload' });
    } catch (error) {
      console.error('Failed to upload image:', error);
      if (isUploadCancelledError(error)) {
        message.destroy('upload');
        return false;
      }
      message.error({ content: error?.message || 'Failed to upload image', key: 'upload' });
    } finally {
      setIsUploading(false);
    }

    return false;
  };

  const modalContent = (
    <>
      <CompressionConfirmModal
        open={compressConfirmState.open}
        fileSize={compressConfirmState.fileSize}
        zIndex={THUMBNAIL_COMPRESS_MODAL_Z}
        getContainer={() => document.body}
        onCompress={() => resolveCompressChoice(true)}
        onUploadOriginal={() => resolveCompressChoice(false)}
        onCancel={() => resolveCompressChoice(null)}
      />
      <Modal
        title={<div className="thumbnail-modal-header"><CameraOutlined className="thumbnail-modal-icon" />Thumbnail Preview</div>}
        open={isModalVisible}
        onCancel={handleCancel}
        className="thumbnail-modal"
        width={560}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleConfirmUpdate} className="update-thumbnail-btn">
            Save Thumbnail
          </Button>,
        ]}
      >
        <div className="thumbnail-content">
          <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
            {(customUrl || previewUrl || existingThumbnail) ? (
              <img src={customUrl || previewUrl || existingThumbnail} alt="Thumbnail Preview" className="thumbnail-preview" />
            ) : (
              <div className="thumbnail-placeholder">
                <div className="placeholder-icon">📄</div>
                <p>No thumbnail yet</p>
              </div>
            )}
          </div>
          <div className="upload-section">
            <div className="upload-label">Upload a custom thumbnail</div>
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
            >
              <Button icon={<UploadOutlined />} className="upload-btn" loading={isUploading} disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Upload Custom Thumbnail'}
              </Button>
            </Upload>
            <div className="capture-label">Capture current view</div>
            <Button icon={<CameraOutlined />} onClick={handleCapture} className="capture-btn" loading={isCapturing} disabled={isCapturing}>
              {isCapturing ? 'Capturing...' : 'Capture Current View'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );

  if (!isTemplateDocument) return null;

  if (viewMode === "menu") {
    return (
      <>
        <div className='menu-item' onClick={() => { handleOpenModal(); onMenuClick && onMenuClick(); }}>
          <CameraOutlined className="toggle-menu-icon" />
          <span>Update Thumbnail</span>
        </div>
        {modalContent}
      </>
    );
  }

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      {modalContent}
    </>
  );
};

export default TemplateThumbnail;